import { Injectable } from '@angular/core';
import { AuthApiService } from '@shared/apis/auth-api.service';
import { BehaviorSubject, EMPTY, Observable, timer, Subscription } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import Constants from '@shared/constants/constants';
import { LookupApiService } from '@shared/apis/lookup-api.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  //private user: Observable<firebase.User>;
  //private userDetails: firebase.User = null;

  readonly tokenKeyName = 'token';

  private token: any;
  private permits: any;
  private groups: any;
  private sites: any;
  public token$ = new BehaviorSubject({});

  public redirectUrl: any;

  // Session Extension Variables
  private refreshTokenTimeout?: Subscription;
  private lastActivityTime: number = Date.now();
  private readonly IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

  constructor(private router: Router,
    private authApiService: AuthApiService,
    private lookupApiService: LookupApiService,
  ) {
    const token = this.loadToken();

    // extend token if LONG_TTL_TOKEN
    if ((token?.permits || []).includes(Constants.Permits.LONG_TTL_TOKEN) && token.token?.refresh_token) {
      setTimeout(() => {
        this.authApiService.refreshToken(token.token.refresh_token).subscribe(
          res => {
            this.setToken(res);
          }
        )
      }, 1000);
    }

    // Update activity on page navigation
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateActivity();
    });
  }

  get deviceId(): string {
    let id = localStorage.getItem('deviceId');
    if (!id) {
      id = this.generateUuid();
      localStorage.setItem('deviceId', id);
    }
    return id;
  }

  set deviceId(value: string | null) {
    if (!value) {
      localStorage.removeItem('deviceId');
    } else {
      localStorage.setItem('deviceId', value);
    }
  }

  private generateUuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  login(username: any, password: any, deviceId: any): Observable<boolean> {
    return this.authApiService.login(username, password, deviceId)
      .pipe(
        map(token => {
          this.setToken(token);
          return token;
        })
      );
  }

  otpLogin(username: any, code: any, tfaToken: any, trust: any): Observable<boolean> {
    return this.authApiService.otp(username, code, tfaToken, trust)
      .pipe(
        map(token => {
          this.setToken(token);
          this.deviceId = token.deviceId;
          return token;
        })
      );
  }

  resendOtp(username: any, tfaToken: any): Observable<boolean> {
    return this.authApiService.resendOtp(username, tfaToken)
      .pipe(
        map(token => {
          this.setToken(token);
          this.deviceId = token.deviceId;
          return token;
        })
      );
  }

  resetPassword(email: string): Observable<Object> {
    return this.authApiService.requestCode(email);
  }

  validateCode(email: string, code: string): Observable<Object> {
    return this.authApiService.validateCode(email, code);
  }

  changePassword(email: string, code: string, password: string): Observable<Object> {
    return this.authApiService.changePassword(email, code, password);
  }

  isAuthenticated() {
    return this.getToken()?.access_token != null;
  }

  logout() {
    this.stopRefreshTokenTimer();
    this.authApiService.logout(this.deviceId).subscribe({
      next: () => {
        this.setToken(null);
        this.deviceId = null;
      },
      error: (err) => {
        console.error(err);
        this.setToken(null);
        this.router.navigate(['/']);
      }
    });
  }

  setToken(token: any, rememberMe?: boolean): void {
    if (token) {
      const tokenStr = JSON.stringify(token);
      if ((token.permits || []).includes(Constants.Permits.LONG_TTL_TOKEN) || rememberMe) {
        localStorage.setItem(this.tokenKeyName, tokenStr);
        sessionStorage.removeItem(this.tokenKeyName);
      } else {
        sessionStorage.setItem(this.tokenKeyName, tokenStr);
        localStorage.removeItem(this.tokenKeyName);
      }
    } else {
      sessionStorage.removeItem(this.tokenKeyName);
      localStorage.removeItem(this.tokenKeyName);
      this.stopRefreshTokenTimer();
    }

    this.processToken(token);

    if (token) {
      this.startRefreshTokenTimer();
    }
  }

  getToken() {
    if (this.token) {
      return this.token;
    }
    return this.loadToken();
  }

  private loadToken() {
    const tokenStr = sessionStorage.getItem(this.tokenKeyName) || localStorage.getItem(this.tokenKeyName);
    let token = null;
    if (tokenStr) {
      token = JSON.parse(tokenStr);
      this.processToken(token);
    }
    return token;
  }

  private processToken(token: any): void {
    this.token = token?.token;
    this.permits = token?.permits || [];
    this.groups = token?.groups || [];
    this.sites = token?.sites || [];
    this.token$.next(this.token);
    if (token === null) {
      this.router.navigate(['/']);
    }
  }

  getPermits() {
    return this.permits;
  }

  hasPermits(permits: string[]) {
    return (this.permits || []).some((x: any) => permits.includes(x));
  }

  inGroups(groups: string[]) {
    return (this.groups || []).some((x: any) => groups.includes(x));
  }

  getSites() {
    return this.sites;
  }

  getAccessToken() {
    const token = this.getToken();
    return token ? token.access_token : '';
  }

  refreshToken(): Observable<any> {
    const token = this.getToken();

    if (!token || !token.refresh_token) {
      return EMPTY;
    }

    return this.authApiService.refreshToken(token.refresh_token);
  }

  postLogIn() {
    let nextUrl = this.redirectUrl || '/dashboard';

    this.lookupApiService.getConstants().subscribe({
      next: (res) => {
        Object.assign(Constants, res);

        // If user has no sites, redirect to site management to create one
        if (!this.sites || this.sites.length === 0) {
          nextUrl = '/master/sites';
        }

        console.log(nextUrl);
        this.router.navigate([nextUrl]);
      },
      error: (err) => {
        console.error(err);
      }
    });
  }

  // region # MFA
  qrCode(): Observable<any> {
    return this.authApiService.qrCode()
      .pipe(
        map(res => {
          return res;
        })
      );
  }

  mfaRegister(code: any): Observable<boolean> {
    return this.authApiService.mfaRegister(code)
      .pipe(
        map(result => {
          return result.result;
        })
      );
  }

  mfaLogin(username: any, code: any, tfaToken: any, trust: any): Observable<boolean> {
    return this.authApiService.mfa(username, code, tfaToken, trust)
      .pipe(
        map(token => {
          this.setToken(token);
          this.deviceId = token.deviceId;
          return token;
        })
      );
  }

  // endregion

  // region # Session Extension

  public updateActivity(): void {
    this.lastActivityTime = Date.now();
  }

  private startRefreshTokenTimer(): void {
    this.stopRefreshTokenTimer();

    const tokenData = this.getToken();
    if (!tokenData || !tokenData.access_token) return;

    try {
      // Decode JWT payload to find 'exp'
      const payload = tokenData.access_token.split('.')[1];
      const decodedPayload = JSON.parse(window.atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

      if (!decodedPayload.exp) return;

      const expires = new Date(decodedPayload.exp * 1000);
      const timeout = expires.getTime() - Date.now() - (60 * 1000); // Trigger 1 minute before expiry

      if (timeout > 0) {
        this.refreshTokenTimeout = timer(timeout).subscribe(() => {
          const timeSinceLastActivity = Date.now() - this.lastActivityTime;

          if (timeSinceLastActivity < this.IDLE_TIMEOUT_MS) {
            console.log('Session extension: User activity detected. Refreshing token.');
            this.refreshToken().subscribe({
              next: (newTokenData) => {
                this.setToken(newTokenData);
              },
              error: (err) => {
                console.error('Session extension failed.', err);
                this.logout();
              }
            });
          } else {
            console.log('Session extension: Idle timeout reached. Logging out.');
            this.logout();
            alert('Your session has expired due to inactivity.');
          }
        });
      }
    } catch (e) {
      console.error('Failed to parse JWT for automatic session extension.', e);
    }
  }

  private stopRefreshTokenTimer(): void {
    if (this.refreshTokenTimeout) {
      this.refreshTokenTimeout.unsubscribe();
      this.refreshTokenTimeout = undefined;
    }
  }

  // endregion
}
